// The assignee filter (docs/design/actions.md, A refined): one toolbar button showing a stack of the
// avatars of the people who have open items here, most items first (at most 4, then "+N"; the dashed
// one last when some open items are nobody's). Clicking it opens a popover (a bottom sheet on a phone)
// with a search box, Me first, then everyone with open items here, then the other active people, each
// with their count, and Unassigned: checkboxes, so it picks several, like Jira. While some are picked
// the button shows only them, ringed, with × to clear. The same avatars as the cards'.

import type { BoardItem, BoardMember, Viewer } from "@boem/core/shared";
import { useRef, useState } from "react";
import { NobodyAvatar, PersonAvatar } from "./Assign.tsx";
import { Icon } from "./Icons.tsx";
import { Popover } from "./Menu.tsx";

export const NOBODY = "__none";
/** At most this many faces on the button; the rest are "+N". */
export const STACK_MAX = 4;

/** What the filter counts of an item: whether it's open, and who has it. */
export type AssigneeCounted = Pick<BoardItem, "status"> & { assignee: { name: string } | null; assignees?: { name: string }[] };

/** Open items per person here (by name), and how many are nobody's. `all`: count every item (the archive). */
export function assigneeCounts(items: AssigneeCounted[], all = false): { by: Map<string, number>; nobody: number } {
  const by = new Map<string, number>();
  let nobody = 0;
  for (const it of items) {
    if (!all && it.status !== "open") continue;
    // an item with several people counts for each of them (OEM-285)
    const names = it.assignees?.map((a) => a.name) ?? (it.assignee ? [it.assignee.name] : []);
    for (const name of names) by.set(name, (by.get(name) ?? 0) + 1);
    if (names.length) continue;
    nobody++;
  }
  return { by, nobody };
}

/** The button's faces: the people with the most open items first (then by name), at most 4, and how many more. */
export function assigneeStack(counts: { by: Map<string, number>; nobody: number }, max = STACK_MAX): { faces: { name: string; n: number }[]; more: number; nobody: boolean } {
  const all = [...counts.by].map(([name, n]) => ({ name, n })).sort((a, b) => b.n - a.n || a.name.localeCompare(b.name));
  return { faces: all.slice(0, max), more: Math.max(0, all.length - max), nobody: counts.nobody > 0 };
}

/** The popover's choices: Me, everyone with open items here (most first), the other active people, Unassigned. */
export function assigneeChoices(counts: { by: Map<string, number>; nobody: number }, members: BoardMember[], viewer: Pick<Viewer, "name">) {
  const people = members.filter((m) => !m.isBot);
  const n = (name: string) => counts.by.get(name) ?? 0;
  const here = [...counts.by.keys()].filter((name) => name !== viewer.name).sort((a, b) => n(b) - n(a) || a.localeCompare(b));
  const others = people.map((m) => m.name).filter((name) => name !== viewer.name && !counts.by.has(name)).sort((a, b) => a.localeCompare(b));
  return {
    me: { name: viewer.name, n: n(viewer.name) },
    here: here.map((name) => ({ name, n: n(name) })),
    others: others.map((name) => ({ name, n: 0 })),
    nobody: counts.nobody,
  };
}

export function AssigneeFilter({
  items,
  members,
  viewer,
  value,
  onChange,
  compact = false,
  all = false,
}: {
  /** the items of this view (its board, its project), before the filters */
  items: AssigneeCounted[];
  members: BoardMember[];
  viewer: Pick<Viewer, "name">;
  value: string[];
  onChange: (next: string[]) => void;
  /** a panel's filter row: 16px faces */
  compact?: boolean;
  /** count every item, not only open ones (the archive) */
  all?: boolean;
}) {
  const btn = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const counts = assigneeCounts(items, all);
  const stack = assigneeStack(counts);
  const choices = assigneeChoices(counts, members, viewer);
  const picked = new Set(value);
  const find = q.trim().toLowerCase();
  const fits = (name: string) => !find || name.toLowerCase().includes(find) || (name === NOBODY && "unassigned nobody".includes(find));
  const toggle = (name: string) => onChange(picked.has(name) ? value.filter((v) => v !== name) : [...value, name]);
  const label = (name: string) => (name === NOBODY ? "Unassigned" : name === viewer.name ? `${name} (me)` : name);
  const tip = value.length ? `Assignee: ${value.map(label).join(", ")}` : "Assignee";
  const face = (name: string, ring = false) => (name === NOBODY ? <NobodyAvatar sm={compact} /> : <PersonAvatar name={name} sm={compact} me={ring} />);
  const row = (name: string, n: number, text = label(name)) => (
    <button
      key={name}
      type="button"
      className="pk-row af-row"
      role="menuitemcheckbox"
      aria-checked={picked.has(name)}
      hidden={!fits(name === NOBODY ? NOBODY : name)}
      onClick={() => toggle(name)}
    >
      <span className="af-box" aria-hidden="true">
        {picked.has(name) && <Icon name="check" />}
      </span>
      {face(name)}
      <span className="nm">{text}</span>
      <span className="af-n">{n}</span>
    </button>
  );
  const visible = [choices.me.name, ...choices.here.map((x) => x.name), ...choices.others.map((x) => x.name), ...(choices.nobody ? [NOBODY] : [])].filter(fits);
  return (
    <span className={`af${compact ? " af-sm" : ""}${value.length ? " af-on" : ""}`}>
      <button
        ref={btn}
        type="button"
        className="chip af-btn tip"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={tip}
        data-tip={tip}
        onClick={() => {
          setQ("");
          setOpen(!open);
        }}
      >
        <span className="af-stack" aria-hidden="true">
          {value.length ? (
            value.map((name) => <span key={name}>{face(name, true)}</span>)
          ) : stack.faces.length || stack.nobody ? (
            <>
              {stack.faces.map((x) => (
                <span key={x.name}>{face(x.name)}</span>
              ))}
              {stack.more > 0 && <span className="af-more">+{stack.more}</span>}
              {stack.nobody && <span>{face(NOBODY)}</span>}
            </>
          ) : (
            <span>{face(NOBODY)}</span>
          )}
        </span>
      </button>
      {value.length > 0 && (
        <button type="button" className="af-x" aria-label="Clear the assignee filter" title="Clear the assignee filter" onClick={() => onChange([])}>
          <Icon name="close" />
        </button>
      )}
      {open && (
        <Popover anchor={btn} onClose={() => setOpen(false)} label="Assignee" role="dialog" className="apick af-pop" focus=".pk-search" sheetHead={<b>Assignee</b>}>
          <input
            type="search"
            className="pk-search"
            placeholder="Search people…"
            aria-label="Search people"
            autoComplete="off"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && visible.length === 1) {
                e.preventDefault();
                toggle(visible[0]!);
              }
            }}
          />
          <div role="menu" aria-label="Assignee">
            <div role="group" aria-label="Me" hidden={!fits(choices.me.name)}>
              {row(choices.me.name, choices.me.n, `${choices.me.name} (me)`)}
            </div>
            {choices.here.length > 0 && (
              <div role="group" aria-label="With open items here" hidden={!choices.here.some((x) => fits(x.name))}>
                <div className="pk-sec" aria-hidden="true">
                  With open items here
                </div>
                {choices.here.map((x) => row(x.name, x.n))}
              </div>
            )}
            {choices.others.length > 0 && (
              <div role="group" aria-label="Everyone else" hidden={!choices.others.some((x) => fits(x.name))}>
                <div className="pk-sec" aria-hidden="true">
                  Everyone else
                </div>
                {choices.others.map((x) => row(x.name, x.n))}
              </div>
            )}
            <div role="group" aria-label="Nobody" hidden={!fits(NOBODY)}>
              <div className="cm-sep" role="separator" />
              {row(NOBODY, choices.nobody)}
            </div>
          </div>
          <div className="af-foot">
            <button type="button" className="btn" disabled={!value.length} onClick={() => onChange([])}>
              Clear
            </button>
          </div>
        </Popover>
      )}
    </span>
  );
}
